import { Component, OnInit, ViewChild, ElementRef, OnDestroy, HostListener, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import * as _ from 'lodash';
import { Observable, Subscription } from 'rxjs';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import { MonitorMenuService } from '../../core/services/dialog-services/monitor-menu.service';
import { TagService } from '../../core/services/http-services/tag.service';
import { DeleteDialogService } from '../../core/services/dialog-services/delete-dialog.service';

import { BehaviorScreen } from '../../core/models/view-models/monitor-models/screen.type';
import { ScreenContentStatus } from '../../core/enums/screen-status.enum';
import { ScreenContentMode } from '../../core/enums/content-mode.enum';
import { StartOptions } from '../../core/models/enums/start-options.enum';
import { MonitorKey } from '../../core/models/view-models/monitor-models/monitor-key.type';
import { TagViewModel } from '../../core/models/view-models/tag-models/tag-view.model';
import { MonitorTagDialogMessagesEnum } from '../../core/enums/toastr-messages/monitor-tag-dialog-messages.enum';
import { MonitorMenuDialogDessagesEnum } from '../../core/enums/toastr-messages/monitor-menu-dialog-messages.enum';
import { RemoteControlService } from '../../core/services/http-services/remote-control.service';
import { MonitorHub } from '../../shared-module/hubs/monitor.hub';
import { ModalFullScreenLinkGeneratorComponent } from '../../shared-module/components/_modals/modal-full-screen-view-link/modal-full-screen-link-generator.component';

@Component({
  selector: 'monitor-menu',
  styleUrls: ['./monitor-menu.component.scss'],
  templateUrl: './monitor-menu.component.html'
})

export class MonitorMenuComponent implements OnInit, OnDestroy {
  @ViewChild('renameInput') renameInput: ElementRef;
  monitor: BehaviorScreen;
  serviceRef: MonitorMenuService;
  isBroadcasting = false;
  title: string;
  isRemoteControlEnabled$: Observable<boolean>;

  screenStatuses = ScreenContentStatus;
  contentModes = ScreenContentMode;
  startOptions = StartOptions;
  public MonitorTagDialogMessagesEnum = MonitorTagDialogMessagesEnum;
  public assignedMonitorTags: TagViewModel[] = [];
  public isAllowToClose: boolean = true;

  private subscriptions: Subscription = new Subscription();
  private currentMonitorKey: MonitorKey = null;

  @HostListener('document:click', ['$event']) onDocumentclick(event: MouseEvent): void {
    if (!(event.target as HTMLElement).classList.contains('click-inside')) {
      this.clickedOutside();
    }
  }

  @HostListener('document:contextmenu', ['$event']) onDocumentContextmenu(event: MouseEvent): void {
    this.clickedOutside(event);
  }

  constructor(
    public toastrService: ToastrService,
    public readonly tagService: TagService,
    public readonly deleteDialogService: DeleteDialogService,
    public readonly router: Router,
    public readonly dialog: MatDialog,
    public readonly remoteControlService: RemoteControlService,
    public readonly monitorHub: MonitorHub,
    public readonly renderer: Renderer2
  ) { }

  ngOnInit(): void {
    this.title = this.monitor.MonitorTitle;
    this.isRemoteControlEnabled$ = this.remoteControlService.isRemoteControlAllowed;

    this.currentMonitorKey = new MonitorKey(this.monitor.MonitorHandler, this.monitor.MachinePhysicalAddress);
    this.subscriptions.add(
      this.tagService.getMonitorTagByMonitorKey(this.currentMonitorKey)
        .subscribe((data: TagViewModel[]) => this.assignedMonitorTags = data)
    );
  }

  refreshStartOptions(target: StartOptions): void {
    this.monitor.changeStartOptions(target);
  }

  onInputClick(): void {
    this.renameInput.nativeElement.select();
  }

  onEnterPress(keyCode: KeyboardEvent): void {
    if (keyCode.keyCode === 13) {
      this.saveTitle();
    }
  }

  onRemoteControl(): void {
    this.disableMouseCursor();
    this.monitorHub.proxy.invoke('AddRemoteControlGroup', this.currentMonitorKey.MachinePhysicalAddress);
    this.remoteControlService.remoteControlMonitorKey.next(this.currentMonitorKey);
  }

  openFullScreenView(): void {
    this.dialog.open(ModalFullScreenLinkGeneratorComponent, {
      data: this.monitor,
      width: '50vw',
      autoFocus: false
    });
  }

  unassignedMonitor(monitorTag: TagViewModel): void {
    this.isAllowToClose = false;
    this.subscriptions.add(
      this.deleteDialogService.show(MonitorTagDialogMessagesEnum.UnAssignMonitorFromMonitorTagTitle)
        .subscribe((isToUnassign: boolean) => {
          if (isToUnassign) {
            this.subscriptions.add(
              this.tagService.unAssignMonitorTagFromMonitor({
                MonitorKeys: [this.currentMonitorKey],
                MonitorTagId: monitorTag.Id
              }).subscribe(() => {
                this.toastrService.success(`${monitorTag.Title} ${MonitorTagDialogMessagesEnum.SuccessfullyUnAssigned} ${this.monitor.MonitorTitle}`);
                _.remove(this.assignedMonitorTags, monitorTag);
                this.isAllowToClose = true;
              }));
          } else {
            this.isAllowToClose = true;
          }
        })
    );
  }

  saveTitle(): void {
    if (this.title === '') {
      this.title = this.monitor.MonitorTitle;
      this.toastrService.error(`You\`ve entered empty title`, 'Rename screen');
    } else if (this.title !== this.monitor.MonitorTitle) {
      this.monitor.renameScreen(this.title).subscribe(() => {
        this.toastrService.success(`${MonitorMenuDialogDessagesEnum.MonitorRenamedDescription} '${this.monitor.MonitorTitle}'`,
          MonitorMenuDialogDessagesEnum.MonitorRenamedTitle);
        this.monitor.MonitorTitle = this.title;
      });
      this.renameInput.nativeElement.blur();
    }
    this.clickedOutside();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private clickedOutside(event = null): void {
    if (event) {
      event.preventDefault();
    } else if (this.isAllowToClose) {
      this.serviceRef.closeDialog();
    }
  }

  private disableMouseCursor(): void {
    this.renderer.addClass(document.body, 'cursor-not-allowed');
    this.renderer.addClass(document.querySelector('app-root'), 'disable-cursor');
    this.renderer.addClass(document.querySelector('.filters-bar'), 'additionall-padding');
  }
}
